/**
 * Modern Accessible Tabs Component
 *
 * This script initializes all tab components on the page. It enables full
 * keyboard navigation, follows ARIA best practices for accessibility, and
 * allows deep-linking to specific tabs using a URL hash (e.g., #my-tab-id).
 */
document.querySelectorAll('.tabs').forEach(($tabBlock) => {
	const $tablist = $tabBlock.querySelector('[role="tablist"]');
	const $tabs = $tabBlock.querySelectorAll('[role="tab"]');
	const $panels = $tabBlock.querySelectorAll('[role="tabpanel"]');

	// If the essential elements aren't found, exit for this block
	if (!$tablist || $tabs.length === 0) {
		return;
	}

	/**
	 * Deactivates all tabs and hides their corresponding panels.
	 */
	const deactivateTabs = () => {
		$tabs.forEach(($t) => {
			$t.setAttribute('aria-selected', 'false');
			$t.setAttribute('tabindex', '-1');
		});
		$panels.forEach(($p) => {
			$p.hidden = true;
		});
	};

	/**
	 * Activates a specific tab and its associated panel.
	 * @param {HTMLElement} $activatedTab The tab element to activate.
	 */
	const activateTab = ($activatedTab) => {
		deactivateTabs();

		const $activatedPanel = $tabBlock.querySelector('#' + $activatedTab.getAttribute('aria-controls'));

		$activatedTab.setAttribute('aria-selected', 'true');
		$activatedTab.removeAttribute('tabindex');
		if ($activatedPanel) {
			$activatedPanel.hidden = false;
		}
		$activatedTab.focus();
	};

	/**
	 * Checks the URL hash and activates the corresponding tab.
	 */
	const activateTabFromHash = () => {
		if (window.location.hash) {
			const tabId = window.location.hash.substring(1);
			const $urlTab = $tabBlock.querySelector(`[aria-controls="${tabId}"]`);

			// Ensure the found tab belongs to this specific tab block before activating
			if ($urlTab && $tabBlock.contains($urlTab)) {
				activateTab($urlTab);
				$urlTab.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}
	};

	// --- Event Listeners ---

	$tablist.addEventListener('click', (event) => {
		const $clickedTab = event.target.closest('[role="tab"]');
		if ($clickedTab) {
			activateTab($clickedTab);
		}
	});

	$tablist.addEventListener('keydown', (event) => {
		const $currentTab = event.target.closest('[role="tab"]');
		if (!$currentTab) return;

		const currentIndex = Array.from($tabs).indexOf($currentTab);
		let newIndex;
		const isVertical = $tablist.getAttribute('aria-orientation') === 'vertical';

		if ((isVertical && event.key === 'ArrowUp') || (!isVertical && event.key === 'ArrowLeft')) {
			event.preventDefault();
			newIndex = (currentIndex - 1 + $tabs.length) % $tabs.length;
		} else if ((isVertical && event.key === 'ArrowDown') || (!isVertical && event.key === 'ArrowRight')) {
			event.preventDefault();
			newIndex = (currentIndex + 1) % $tabs.length;
		} else if (event.key === 'Home') {
			event.preventDefault();
			newIndex = 0;
		} else if (event.key === 'End') {
			event.preventDefault();
			newIndex = $tabs.length - 1;
		}

		if (newIndex !== undefined) {
			activateTab($tabs[newIndex]);
		}
	});

	// --- Initialization ---

	// Activate a tab on initial page load if a hash is present
	activateTabFromHash();

	// Add a listener to activate a tab if the hash changes later
	window.addEventListener('hashchange', activateTabFromHash, false);
});
